import {
   useGetActiveLeadSourceDetails,
   useGetCampaignAssistantsVoices,
} from '@forwardflow/hooks/customHooks'
import { useAddAssistantsModalStore } from '@forwardflow/store/modal/AddAssistantsModal'
import { Button, Spinner } from '@forwardflow/ui'
import { message, Modal } from 'antd'
import { AssistantCard, AssistantCardProps } from './AssistantCard'
import { useState, useMemo } from 'react'
import CampaignService from '@forwardflow/services/CampaignService'
import { useParams } from 'react-router-dom'

export const AddAssistantsModal = () => {
   const { title } = useParams()
   const [isLoading, setIsLoading] = useState(false)
   const [selectedAssistants, setSelectedAssistants] = useState<any[]>([])
   const { isAddAssistantsModalOpen, closeAddAssistantsModal } =
      useAddAssistantsModalStore()
   const { data: newAssistants, isLoading: isLoadingVoices } =
      useGetCampaignAssistantsVoices({
         page: 1,
         limit: 10,
      })
   const { data: leadSourceData, mutate } = useGetActiveLeadSourceDetails(
      title as string
   )

   const availableAssistants = useMemo(() => {
      if (!newAssistants || !leadSourceData?.data?.aiAssistants) return []

      const assignedVoiceIds = leadSourceData.data.aiAssistants.map(
         (assistant: any) => assistant.voiceId
      )

      return newAssistants.filter(
         (assistant: any) => !assignedVoiceIds.includes(assistant.voice_id)
      )
   }, [newAssistants, leadSourceData?.data?.aiAssistants])

   const handleAssistantSelection = (assistant: any, isSelected: boolean) => {
      if (isSelected) {
         setSelectedAssistants((prev) => [...prev, assistant])
      } else {
         setSelectedAssistants((prev) =>
            prev.filter((a) => a.voice_id !== assistant.voice_id)
         )
      }
   }

   const onSubmitAssistants = async (
      selectedAssistants: AssistantCardProps['assistant'][]
   ) => {
      if (selectedAssistants.length === 0) return
      setIsLoading(true)
      try {
         await CampaignService.createOrganizationCampaignAssistantsInBulk(
            title as string,
            selectedAssistants.map((assistant) => ({
               avatar: assistant?.avatar || '',
               assistantName: assistant?.name || '',
               voiceId: assistant?.voice_id || '',
               description: assistant?.description || '',
               designation: 'Sales assistant',
            }))
         )
         mutate()
         message.success('Assistants created successfully')
         setSelectedAssistants([])
         closeAddAssistantsModal()
      } catch {
         message.error('Failed to create assistants')
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <>
         <Modal
            className="p-0 ff-modal md:min-w-[670px]"
            title=""
            open={isAddAssistantsModalOpen}
            onCancel={closeAddAssistantsModal}
            footer={null}
         >
            <div className="px-8 pb-3 mb-5 border-b border-white/20 pt-7">
               <p className="text-[22px] font-medium mb-4 leading-6">
                  Select more Assistants
               </p>
               <p className="text-[#818181] text-[12px] font-medium mb-2">
                  You can select agent based on the voice, accent and other
                  properties and edit their names in the previous screen
               </p>
            </div>

            <div className=" overflow-auto max-h-[500px] px-5">
               {isLoadingVoices ? (
                  <div className="flex items-center justify-center h-full">
                     <Spinner />
                  </div>
               ) : availableAssistants.length === 0 ? (
                  <div className="flex items-center justify-center h-[100px] text-white/70">
                     No more assistants available to add
                  </div>
               ) : (
                  <div className="flex flex-col gap-2 mb-2">
                     {Array.isArray(availableAssistants) &&
                        availableAssistants.map((assistant, index) => (
                           <AssistantCard
                              key={index}
                              assistant={
                                 assistant as AssistantCardProps['assistant']
                              }
                              onSelect={handleAssistantSelection}
                              isSelected={selectedAssistants.some(a => a.voice_id === assistant.voice_id)}
                           />
                        ))}
                  </div>
               )}
            </div>
            <div className="flex items-center justify-between px-8 pt-2 pb-5">
               <Button
                  onClick={closeAddAssistantsModal}
                  className="w-full !mt-5 text-white max-w-[230px] !rounded !bg-transparent hover:!bg-black/30 border !border-white/20"
                  type="button"
               >
                  Cancel
               </Button>
               <Button
                  isLoading={isLoading}
                  isDisabled={isLoading || selectedAssistants.length === 0}
                  className="w-full !mt-5 text-white max-w-[230px] !rounded"
                  type="submit"
                  onClick={() => onSubmitAssistants(selectedAssistants)}
               >
                  Add Assistants
               </Button>
            </div>
         </Modal>
      </>
   )
}
